import { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import { Button, Content, Header, Modal, SubscriptionModal, Text } from '../common/components';
import { useAppDispatch, useAppSelector, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import { selectSecurity } from '../common/store/securitySlice';
import { setFlashMessage } from '../common/store/uiSlice';
import IssueCategorySettingsModal from './components/IssueCategorySettingsModal';
import IssueDetailModal from './components/IssueDetailModal';
import IssueFormModal from './components/IssueFormModal';
import IssueList from './components/IssueList';
import IssueReportCta from './components/IssueReportCta';
import {
  addIssueComment,
  changeIssueStatus,
  createIssue,
  createIssueCategory,
  deleteIssue,
  deleteIssueCategory,
  getIssueCategories,
  getIssues,
  reorderIssueCategories,
  updateIssue,
  updateIssueCategory,
} from './services/IssueService';
import { Issue, IssueCategory, IssueCategoryFormValues, IssueFormValues, IssueStatus } from './types';

const namespace = 'Issues';

type FilterKey = 'all' | 'open' | 'closed';
const filters: FilterKey[] = ['all', 'open', 'closed'];

const matchesFilter = (issue: Issue, filter: FilterKey): boolean => {
  switch (filter) {
    case 'open':
      return issue.status === 'new' || issue.status === 'in_progress';
    case 'closed':
      return issue.status === 'resolved' || issue.status === 'rejected';
    default:
      return true;
  }
};

export const Issues: React.FC = () => {
  useDocumentTitle(t('title', namespace));
  const dispatch = useAppDispatch();

  // Plný modul jen pro building-subscription (SUBSCRIPTION_FULL) – stejné uzamčení jako Termíny.
  const security = useAppSelector(selectSecurity).ui;
  const isAdmin = security.isAdmin === true;
  const hasSubscription = (security.tokens?.filter((item) => item.type === 'building-subscription').length || 0) > 0;

  const [issues, setIssues] = useState<Issue[]>([]);
  const [categories, setCategories] = useState<IssueCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filter, setFilter] = useState<FilterKey>('all');

  const [selected, setSelected] = useState<Issue | null>(null);
  const [isDetailShown, setIsDetailShown] = useState<boolean>(false);
  const [isFormShown, setIsFormShown] = useState<boolean>(false);
  const [isDeleteShown, setIsDeleteShown] = useState<boolean>(false);
  const [isSettingsShown, setIsSettingsShown] = useState<boolean>(false);
  const [editing, setEditing] = useState<Issue | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [issueItems, categoryItems] = await Promise.all([getIssues(), getIssueCategories()]);
        setIssues(issueItems);
        setCategories(categoryItems);
      } catch {
        dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [dispatch]);

  const visibleIssues = useMemo(() => issues.filter((issue) => matchesFilter(issue, filter)), [issues, filter]);

  const upsertIssue = (issue: Issue) => {
    setIssues((prev) => {
      const exists = prev.some((item) => item.id === issue.id);
      return exists ? prev.map((item) => (item.id === issue.id ? issue : item)) : [issue, ...prev];
    });
    setSelected((prev) => (prev && prev.id === issue.id ? issue : prev));
  };

  const openCreate = () => {
    setEditing(null);
    setIsFormShown(true);
  };

  const openDetail = (issue: Issue) => {
    setSelected(issue);
    setIsDetailShown(true);
  };

  const openEdit = (issue: Issue) => {
    setEditing(issue);
    setIsDetailShown(false);
    setIsFormShown(true);
  };

  const openDelete = (issue: Issue) => {
    setSelected(issue);
    setIsDetailShown(false);
    setIsDeleteShown(true);
  };

  const handleSubmit = async (values: IssueFormValues, id: string | null) => {
    try {
      const saved = id ? await updateIssue(id, values) : await createIssue(values);
      upsertIssue(saved);
      setIsFormShown(false);
      dispatch(
        setFlashMessage({
          message: t(id ? 'Flash.successEdit' : 'Flash.successAdd', namespace),
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      throw error;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteIssue(id);
      setIssues((prev) => prev.filter((item) => item.id !== id));
      setIsDeleteShown(false);
      dispatch(setFlashMessage({ message: t('Flash.successDelete', namespace), type: 'success' }));
    } catch {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
    }
  };

  const handleChangeStatus = async (issue: Issue, status: IssueStatus) => {
    try {
      upsertIssue(await changeIssueStatus(issue.id, status));
      dispatch(setFlashMessage({ message: t('Flash.successStatus', namespace), type: 'success' }));
    } catch {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
    }
  };

  const handleAddComment = async (issue: Issue, content: string) => {
    try {
      upsertIssue(await addIssueComment(issue.id, content));
      dispatch(setFlashMessage({ message: t('Flash.successComment', namespace), type: 'success' }));
    } catch {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      throw new Error('comment failed');
    }
  };

  const upsertCategory = (category: IssueCategory) =>
    setCategories((prev) => {
      const exists = prev.some((item) => item.id === category.id);
      return exists ? prev.map((item) => (item.id === category.id ? category : item)) : [...prev, category];
    });

  const handleCategoryCreate = async (values: IssueCategoryFormValues) => {
    try {
      upsertCategory(await createIssueCategory(values));
      dispatch(setFlashMessage({ message: t('Settings.Flash.successAdd', namespace), type: 'success' }));
    } catch (error) {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      throw error;
    }
  };

  const handleCategoryUpdate = async (id: string, values: IssueCategoryFormValues) => {
    try {
      upsertCategory(await updateIssueCategory(id, values));
      dispatch(setFlashMessage({ message: t('Settings.Flash.successEdit', namespace), type: 'success' }));
    } catch (error) {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      throw error;
    }
  };

  const handleCategoryReorder = async (categoryIds: string[]) => {
    // Optimistická změna pořadí; server vrátí autoritativní seznam.
    setCategories((prev) => {
      const byId = new Map(prev.map((item) => [item.id, item]));
      const ordered = categoryIds.map((id) => byId.get(id)).filter(Boolean) as IssueCategory[];
      const rest = prev.filter((item) => !categoryIds.includes(item.id));
      return [...ordered, ...rest];
    });

    try {
      setCategories(await reorderIssueCategories(categoryIds));
    } catch {
      dispatch(setFlashMessage({ message: t('Flash.error', namespace), type: 'error' }));
      try {
        setCategories(await getIssueCategories());
      } catch {
        // ponecháme optimistický stav – reload stránky ho srovná
      }
    }
  };

  const handleCategoryDelete = async (id: string) => {
    try {
      await deleteIssueCategory(id);
      setCategories((prev) => prev.filter((item) => item.id !== id));
      dispatch(setFlashMessage({ message: t('Settings.Flash.successDelete', namespace), type: 'success' }));
    } catch (error) {
      // 409 = výchozí/použitá kategorie – smazat nejde, jen deaktivovat
      dispatch(setFlashMessage({ message: t('Settings.Flash.deleteConflict', namespace), type: 'error' }));
      throw error;
    }
  };

  return (
    <>
      <Header
        title={t('heading', namespace)}
        backlink={-1}
        actions={
          hasSubscription && (
            <>
              <Button size="small" title={t('addAction', namespace)} onClick={openCreate} />
              {isAdmin && (
                <Button
                  size="small"
                  variant="primary-light"
                  iconBefore="settings"
                  title={t('settingsAction', namespace)}
                  onClick={() => setIsSettingsShown(true)}
                />
              )}
            </>
          )
        }
      />

      <Content className="Content Issues" isLoading={isLoading}>
        <div className="IssueLock">
          <div className={cx(!hasSubscription && 'IssueLock__blur')}>
            <IssueReportCta onReport={openCreate} />

            <div className="IssueFilter">
              {filters.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={cx('IssueFilter__item', filter === key && 'IssueFilter__item--active')}
                  onClick={() => setFilter(key)}
                >
                  {t(`filter.${key}`, namespace)}
                </button>
              ))}
            </div>

            <IssueList issues={visibleIssues} onOpen={openDetail} />
          </div>

          {/* Bez předplatného: admin vidí nabídku objednání, member žádost na výbor (licenci kupuje budova). */}
          {!hasSubscription && isAdmin && (
            <SubscriptionModal title={t('adminLock.title', namespace)} text={t('adminLock.text', namespace)} />
          )}
          {!hasSubscription && !isAdmin && (
            <SubscriptionModal
              title={t('memberLock.title', namespace)}
              text={t('memberLock.text', namespace)}
              showOrderButton={false}
            />
          )}
        </div>
      </Content>

      <IssueFormModal
        isShown={isFormShown}
        selected={editing}
        categories={categories}
        onHide={() => setIsFormShown(false)}
        onSubmit={handleSubmit}
      />

      {isAdmin && (
        <IssueCategorySettingsModal
          isShown={isSettingsShown}
          categories={categories}
          onHide={() => setIsSettingsShown(false)}
          onCreate={handleCategoryCreate}
          onUpdate={handleCategoryUpdate}
          onDelete={handleCategoryDelete}
          onReorder={handleCategoryReorder}
        />
      )}

      <IssueDetailModal
        isShown={isDetailShown}
        issue={selected}
        isAdmin={isAdmin}
        onHide={() => setIsDetailShown(false)}
        onEdit={openEdit}
        onDelete={openDelete}
        onChangeStatus={handleChangeStatus}
        onAddComment={handleAddComment}
      />

      <Modal isShown={isDeleteShown} onHide={() => setIsDeleteShown(false)}>
        <Text size="h2" align="center" className="mb-2">
          {t('DeleteModal.title', namespace)}
        </Text>
        <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
          {t('DeleteModal.text', namespace)}
        </Text>
        <div className="Flex FlexColumn FlexAlign--center">
          <Button
            title={t('DeleteModal.confirm', namespace)}
            variant="primary-light"
            wide
            onClick={() => selected && handleDelete(selected.id)}
            disabledDeps={['demo', 'expired']}
          />
        </div>
      </Modal>
    </>
  );
};

export default Issues;
